import React, { useMemo, useState } from "react";
import type { TempUnit } from "../../services/cookingState";
import HomeTempThresholdEditor from "./HomeTempThresholdEditor";
import type { HomeTemperatureThreshold } from "../../services/homeTemperatureThreshold";
import type {
  UnattendedSpeed,
  UserConfigDto,
} from "../../services/userConfig";

/**
 * Shared UserConfig editor. Renders every field the API exposes on
 * `/user-config`, minus Demo Mode (which is install-time only on the
 * sensor). Used by both the per-sensor drawer on SensorDetailPage AND
 * the building-wide bulk-apply modal.
 *
 * Emits `onChange(patch)` where `patch` is a partial [UserConfigDto]
 * containing ONLY the fields the user has touched since the editor
 * mounted. The parent PUTs that patch verbatim; the server preserves
 * every unmentioned field on both DynamoDB and the MQTT publish. That
 * lets the bulk modal target one setting (e.g. "everyone's unattended
 * speed -> slow") without steamrolling the four other fields the user
 * carefully tuned per sensor.
 *
 * Each section has a "Change this" checkbox (default OFF) to make the
 * "I don't want to touch this" intent explicit -- otherwise a user who
 * clicks into a slider "just to see what's there" would ship that
 * value to every sensor at the property. Turning the checkbox off
 * removes the field from the emitted patch even if the value was
 * previously edited.
 *
 * For the per-sensor drawer, pass `initial` seeded with the sensor's
 * current values; the caller can auto-tick every section so the user
 * can just edit and save. For bulk, pass sensible defaults with no
 * sections auto-ticked (the default).
 */
type SectionKey =
  | "ambientAlerts"
  | "unattendedSpeed"
  | "auxLights"
  | "speakerVolume"
  | "buttonLockout";

type Props = {
  /** Initial values shown when a section is ticked on; used as the
   * baseline for the sensor-details drawer where the current config
   * is fetched and rendered. Missing fields fall back to safe UI
   * defaults (see [DEFAULTS]). */
  initial?: UserConfigDto | null;
  /** Which sections start ticked. Empty for bulk (default), keys of
   * every field with a value in `initial` for the per-sensor drawer. */
  initiallyTicked?: SectionKey[];
  /** Fires on every change. Contains ONLY ticked+edited fields. */
  onChange: (patch: UserConfigDto) => void;
  unit: TempUnit;
  disabled?: boolean;
};

const DEFAULTS = {
  ambientAlerts: { enabled: false, lowC: 18, highC: 27 } as {
    enabled: boolean;
    lowC: number;
    highC: number;
  },
  unattendedSpeed: "default" as UnattendedSpeed,
  auxLights: true,
  speakerVolume: 80,
  buttonLockout: false,
};

export default function UserConfigEditor({
  initial,
  initiallyTicked,
  onChange,
  unit,
  disabled,
}: Props) {
  // Working state per section: seeded from `initial`, falling back to
  // DEFAULTS. Modelled separately from `ticked` so unticking then
  // reticking a section preserves the user's in-flight edits.
  const [ambient, setAmbient] = useState<HomeTemperatureThreshold>(() => {
    const aa = initial?.ambientAlerts ?? DEFAULTS.ambientAlerts;
    return {
      enabled: !!aa.enabled,
      minC: Number(aa.lowC),
      maxC: Number(aa.highC),
    };
  });
  const [speed, setSpeed] = useState<UnattendedSpeed>(
    (initial?.unattendedSpeed as UnattendedSpeed | null | undefined) ??
      DEFAULTS.unattendedSpeed
  );
  const [lights, setLights] = useState<boolean>(
    initial?.auxLights ?? DEFAULTS.auxLights
  );
  const [volume, setVolume] = useState<number>(
    initial?.speakerVolume ?? DEFAULTS.speakerVolume
  );
  const [lockout, setLockout] = useState<boolean>(
    initial?.buttonLockout ?? DEFAULTS.buttonLockout
  );

  // CONTRACT, stated because it was only inferable from the absence of a
  // useEffect: this component is correct ONLY when mounted after its data has
  // resolved, and it never re-seeds. `initial` is snapshotted at mount exactly
  // like `initiallyTicked`. A caller that mounts it before the fetch resolves,
  // or that lets the query refetch underneath it, gets stale seeds and a save
  // that writes them back. SensorUserConfigCard holds up its end by gating on
  // isPending and using staleTime: Infinity.
  //
  // We want the initial set snapshotted once at mount; further prop changes
  // shouldn't wipe the user's in-flight toggles.
  const initialTickedSet = useMemo(
    () => new Set<SectionKey>(initiallyTicked ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [ticked, setTicked] = useState<Set<SectionKey>>(initialTickedSet);

  // Emit a partial patch every time something changes. Only sections
  // the user has explicitly ticked ON contribute.
  const emit = (nextTicked: Set<SectionKey>, overrides?: Partial<{
    ambient: HomeTemperatureThreshold;
    speed: UnattendedSpeed;
    lights: boolean;
    volume: number;
    lockout: boolean;
  }>) => {
    const a = overrides?.ambient ?? ambient;
    const s = overrides?.speed ?? speed;
    const l = overrides?.lights ?? lights;
    const v = overrides?.volume ?? volume;
    const lk = overrides?.lockout ?? lockout;
    const patch: UserConfigDto = {};
    if (nextTicked.has("ambientAlerts")) {
      patch.ambientAlerts = {
        enabled: a.enabled,
        lowC: a.minC,
        highC: a.maxC,
      };
    }
    if (nextTicked.has("unattendedSpeed")) patch.unattendedSpeed = s;
    if (nextTicked.has("auxLights")) patch.auxLights = l;
    if (nextTicked.has("speakerVolume")) patch.speakerVolume = v;
    if (nextTicked.has("buttonLockout")) patch.buttonLockout = lk;
    onChange(patch);
  };

  const toggle = (key: SectionKey, on: boolean) => {
    const next = new Set(ticked);
    if (on) next.add(key);
    else next.delete(key);
    setTicked(next);
    emit(next);
  };

  return (
    <div className={`flex flex-col gap-5 ${disabled ? "opacity-60" : ""}`}>
      {/* Ambient alerts -- the section toggle IS the on/off. When ticked
          we always emit enabled=true; unticking the section leaves the
          sensor's existing alert state alone (nothing shipped in the
          patch). Turning alerts off explicitly is a phone-app job. */}
      <Section
        title="Home Temperature Alerts"
        subtitle="Notify when the room drifts outside the comfort range."
        checked={ticked.has("ambientAlerts")}
        onToggle={(on) => toggle("ambientAlerts", on)}
        disabled={disabled}
      >
        <div className="max-w-xs mx-auto">
          <HomeTempThresholdEditor
            value={{ ...ambient, enabled: true }}
            onChange={(next) => {
              const withEnabled = { ...next, enabled: true };
              setAmbient(withEnabled);
              emit(ticked, { ambient: withEnabled });
            }}
            unit={unit}
            disabled={disabled || !ticked.has("ambientAlerts")}
            hideEnabledToggle
          />
        </div>
      </Section>

      {/* Unattended alert speed */}
      <Section
        title="Unattended Alert Speed"
        subtitle="How quickly the sensor flags an unattended stove."
        checked={ticked.has("unattendedSpeed")}
        onToggle={(on) => toggle("unattendedSpeed", on)}
        disabled={disabled}
      >
        <div className="flex justify-center">
          <UnattendedSpeedControl
            value={speed}
            onChange={(next) => {
              setSpeed(next);
              emit(ticked, { speed: next });
            }}
            disabled={disabled || !ticked.has("unattendedSpeed")}
          />
        </div>
      </Section>

      {/* Indicator lights */}
      <Section
        title="Indicator Lights"
        subtitle="Presence and cooking-colour LEDs only. Danger alert lights always stay on."
        checked={ticked.has("auxLights")}
        onToggle={(on) => toggle("auxLights", on)}
        disabled={disabled}
      >
        <div className="flex justify-center">
          <TwoSidedToggle
            value={lights}
            offLabel="Off"
            onLabel="On"
            onChange={(next) => {
              setLights(next);
              emit(ticked, { lights: next });
            }}
            disabled={disabled || !ticked.has("auxLights")}
          />
        </div>
      </Section>

      {/* Speaker volume */}
      <Section
        title="Speaker Volume"
        subtitle="Voice prompts, confirmation chimes, and alarm tones. Minimum 30% so danger warnings stay audible."
        checked={ticked.has("speakerVolume")}
        onToggle={(on) => toggle("speakerVolume", on)}
        disabled={disabled}
      >
        <div className="flex justify-center">
          <VolumeControl
            value={volume}
            onChange={(next) => {
              setVolume(next);
              emit(ticked, { volume: next });
            }}
            disabled={disabled || !ticked.has("speakerVolume")}
          />
        </div>
      </Section>

      {/* Button tamper lock */}
      <Section
        title="Button Tamper Lock"
        subtitle="Prevents resetting the sensor by holding its physical button. When Locked, a reset must be done by an authorized user in the app."
        checked={ticked.has("buttonLockout")}
        onToggle={(on) => toggle("buttonLockout", on)}
        disabled={disabled}
      >
        <div className="flex justify-center">
          <TwoSidedToggle
            value={lockout}
            offLabel="Unlocked"
            onLabel="Locked"
            onChange={(next) => {
              setLockout(next);
              emit(ticked, { lockout: next });
            }}
            disabled={disabled || !ticked.has("buttonLockout")}
          />
        </div>
      </Section>
    </div>
  );
}

// ─── Section wrapper ────────────────────────────────────────────────

type SectionProps = {
  title: string;
  subtitle: string;
  checked: boolean;
  onToggle: (on: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
};

function Section({
  title,
  subtitle,
  checked,
  onToggle,
  disabled,
  children,
}: SectionProps) {
  return (
    <div
      className={`rounded-xl border p-4 transition-colors ${
        checked
          ? "border-black/15 bg-white"
          : "border-black/10 bg-black/[0.02]"
      }`}
    >
      <label className="flex items-start gap-3 select-none cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onToggle(e.target.checked)}
          disabled={disabled}
          className="mt-1 h-4 w-4 accent-[#BBDA62]"
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-ink">{title}</div>
          <div className="text-xs text-ink-3 leading-snug mt-0.5">
            {subtitle}
          </div>
        </div>
      </label>
      {checked ? <div className="mt-3">{children}</div> : null}
    </div>
  );
}

// ─── Unattended-speed 4-way segmented control ───────────────────────

const SPEED_ORDER: Array<{ key: UnattendedSpeed; label: string }> = [
  { key: "fast", label: "Faster" },
  { key: "default", label: "Default" },
  { key: "slow", label: "Slower" },
  { key: "off", label: "Off" },
];

function UnattendedSpeedControl({
  value,
  onChange,
  disabled,
}: {
  value: UnattendedSpeed;
  onChange: (next: UnattendedSpeed) => void;
  disabled?: boolean;
}) {
  const handlePick = (next: UnattendedSpeed) => {
    if (next === value) return;
    if (next === "off") {
      const ok = window.confirm(
        "Turn OFF unattended alerts?\n\n" +
          "The sensor will no longer flag a stove left running while no one " +
          "is in the room. Other safety features stay on."
      );
      if (!ok) return;
    }
    onChange(next);
  };
  return (
    <div className="inline-flex rounded-control border border-black/15 overflow-hidden">
      {SPEED_ORDER.map((opt) => {
        const active = value === opt.key;
        const isOff = opt.key === "off";
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => handlePick(opt.key)}
            disabled={disabled}
            className={`px-3 py-1.5 text-sm font-medium transition-colors border-r last:border-r-0 border-black/10 ${
              active
                ? isOff
                  ? "bg-alert-critical text-white"
                  : "bg-[#BBDA62] text-ink"
                : "bg-white text-ink-2 hover:bg-black/[0.04]"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Two-sided pill toggle (both options visible) ────────────────────

function TwoSidedToggle({
  value,
  offLabel,
  onLabel,
  onChange,
  disabled,
}: {
  value: boolean;
  offLabel: string;
  onLabel: string;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="inline-flex rounded-control border border-black/15 overflow-hidden">
      {[
        { label: offLabel, active: value === false, next: false },
        { label: onLabel, active: value === true, next: true },
      ].map((opt) => (
        <button
          key={opt.label}
          type="button"
          onClick={() => {
            if (opt.next !== value) onChange(opt.next);
          }}
          disabled={disabled}
          className={`px-4 py-1.5 text-sm font-medium transition-colors border-r last:border-r-0 border-black/10 ${
            opt.active
              ? "bg-[#BBDA62] text-ink"
              : "bg-white text-ink-2 hover:bg-black/[0.04]"
          } disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Speaker volume: discrete segmented control ─────────────────────

// Range is 30..100 (see phone-app cts_custom_provisioning.dart: firmware
// floors anything below 30 to keep alerts audible). Only these stops are
// selectable -- consistent with the unattended-speed control's discrete
// choice model, and matches the phone app which also caps user choice to
// a small set of named levels rather than a continuous slider.
const VOLUME_STOPS = [30, 45, 60, 75, 90, 100];

function VolumeControl({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  // Snap a persisted value (which could be an arbitrary 30..100 int
  // written by the phone app's slider) to the nearest allowed stop for
  // display so exactly one pill lights up.
  const active = VOLUME_STOPS.reduce((best, n) =>
    Math.abs(n - value) < Math.abs(best - value) ? n : best
  );
  return (
    <div className="inline-flex rounded-control border border-black/15 overflow-hidden">
      {VOLUME_STOPS.map((n) => {
        const isActive = active === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            disabled={disabled}
            className={`px-3 py-1.5 text-sm font-medium tabular-nums transition-colors border-r last:border-r-0 border-black/10 ${
              isActive
                ? "bg-[#BBDA62] text-ink"
                : "bg-white text-ink-2 hover:bg-black/[0.04]"
            } disabled:opacity-40 disabled:cursor-not-allowed`}
            aria-label={`Set volume to ${n}%`}
            aria-pressed={isActive}
          >
            {n}%
          </button>
        );
      })}
    </div>
  );
}
